import { OneSignal } from 'react-native-onesignal'

const OS: any = OneSignal as any

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

let registrationInFlight: Promise<PushState> | null = null
let registrationInFlightUserId: string | null = null

export function isOneSignalAvailable(): boolean {
    try {
        return typeof OS?.User?.pushSubscription?.getOptedIn === 'function'
    } catch {
        return false
    }
}

export interface PushState {
    permission: boolean
    optedIn: boolean
    subscriptionId: string | null
    pushToken: string | null
    externalId: string | null
}

export const EMPTY_PUSH_STATE: PushState = {
    permission: false,
    optedIn: false,
    subscriptionId: null,
    pushToken: null,
    externalId: null,
}

// Last known push state, kept up to date by readPushStateAsync() and the event
// listeners in subscribeToPushStateChanges(). readPushStateSync() returns this
// cache instead of calling OneSignal's deprecated synchronous getters
// (hasPermission/getOptedIn/getPushSubscriptionId), which otherwise log a
// deprecation warning on every render that reads push state.
let cachedState: PushState = EMPTY_PUSH_STATE

// Deprecated synchronous readers — only used as a fallback inside
// readPushStateAsync() on older SDKs that lack the *Async methods. On SDKs that
// expose the async API (which is what triggers the deprecation warnings) these
// are never reached.
function readPermissionSync(): boolean {
    try {
        const notifications = OS?.Notifications
        if (!notifications) return false
        if (typeof notifications.hasPermission === 'function') {
            return Boolean(notifications.hasPermission())
        }
        if (typeof notifications.permission === 'boolean') {
            return notifications.permission
        }
        return false
    } catch {
        return false
    }
}

function readOptedInSync(): boolean {
    try {
        const pushSubscription = OS?.User?.pushSubscription
        if (!pushSubscription) return false
        if (typeof pushSubscription.getOptedIn === 'function') {
            return Boolean(pushSubscription.getOptedIn())
        }
        if (typeof pushSubscription.optedIn === 'boolean') {
            return pushSubscription.optedIn
        }
        return false
    } catch {
        return false
    }
}

function readSubscriptionIdSync(): string | null {
    try {
        const pushSubscription = OS?.User?.pushSubscription
        if (!pushSubscription) return null
        if (typeof pushSubscription.getPushSubscriptionId === 'function') {
            const id = pushSubscription.getPushSubscriptionId()
            return id ? String(id) : null
        }
        if (pushSubscription.id) {
            return String(pushSubscription.id)
        }
        return null
    } catch {
        return null
    }
}

function normalizePushState(state: PushState): PushState {
    // OneSignal's hasPermission() returns false on some Android devices even when
    // the user has an active push subscription and receives notifications.
    const permission = state.permission || Boolean(state.optedIn && state.subscriptionId)
    return { ...state, permission }
}

/**
 * Returns the last known push state synchronously. This reads from the cache
 * populated by readPushStateAsync()/event listeners rather than calling
 * OneSignal's deprecated synchronous getters, so it can be called on every
 * render without spamming deprecation warnings.
 */
export function readPushStateSync(): PushState {
    return cachedState
}

export async function readPushStateAsync(): Promise<PushState> {
    try {
        const notifications = OS?.Notifications
        const pushSubscription = OS?.User?.pushSubscription
        const user = OS?.User

        const permissionPromise =
            typeof notifications?.getPermissionAsync === 'function'
                ? notifications.getPermissionAsync()
                : Promise.resolve(readPermissionSync())

        const optedInPromise =
            typeof pushSubscription?.getOptedInAsync === 'function'
                ? pushSubscription.getOptedInAsync()
                : Promise.resolve(readOptedInSync())

        const subscriptionIdPromise =
            typeof pushSubscription?.getIdAsync === 'function'
                ? pushSubscription.getIdAsync()
                : Promise.resolve(readSubscriptionIdSync())

        const pushTokenPromise =
            typeof pushSubscription?.getTokenAsync === 'function'
                ? pushSubscription.getTokenAsync()
                : Promise.resolve(null)

        const externalIdPromise =
            typeof user?.getExternalId === 'function'
                ? user.getExternalId()
                : Promise.resolve(null)

        const [permission, optedIn, subscriptionId, pushToken, externalId] = await Promise.all([
            permissionPromise,
            optedInPromise,
            subscriptionIdPromise,
            pushTokenPromise,
            externalIdPromise,
        ])

        cachedState = normalizePushState({
            permission: Boolean(permission),
            optedIn: Boolean(optedIn),
            subscriptionId: subscriptionId ? String(subscriptionId) : null,
            pushToken: pushToken ? String(pushToken) : null,
            externalId: externalId ? String(externalId) : null,
        })
        return cachedState
    } catch {
        return cachedState
    }
}

export function isPushLikelyDeliverable(state?: PushState): boolean {
    const s = state ?? readPushStateSync()
    if (Boolean(s.optedIn && s.subscriptionId)) return true
    return s.permission
}

/**
 * Push is deliverable only when the device subscription exists, has a push token,
 * and is linked to the signed-in Supabase user via OneSignal external_id.
 */
export function isPushHealthy(state: PushState, expectedExternalId?: string | null): boolean {
    if (!state.permission || !state.optedIn || !state.subscriptionId || !state.pushToken) {
        return false
    }
    if (!expectedExternalId) return Boolean(state.externalId)
    return state.externalId === expectedExternalId
}

export function isPushLinkedToAccount(state: PushState, expectedExternalId: string): boolean {
    return Boolean(state.subscriptionId) && state.externalId === expectedExternalId
}

/** Account login may be set before the device subscription exists (common after OTA). */
export function isPushAccountLoginPending(state: PushState, expectedExternalId: string): boolean {
    return !state.subscriptionId && state.externalId === expectedExternalId
}

async function waitForPushRegistration(options?: {
    timeoutMs?: number
    intervalMs?: number
    expectedExternalId?: string
    requireToken?: boolean
}): Promise<PushState> {
    const timeoutMs = options?.timeoutMs ?? 20000
    const intervalMs = options?.intervalMs ?? 500
    const deadline = Date.now() + timeoutMs

    while (Date.now() < deadline) {
        const state = await readPushStateAsync()
        const hasSubscription = Boolean(state.subscriptionId)
        const hasToken = !options?.requireToken || Boolean(state.pushToken)
        const isLinked = !options?.expectedExternalId
            || state.externalId === options.expectedExternalId

        if (hasSubscription && hasToken && isLinked) return state
        await sleep(intervalMs)
    }

    return readPushStateAsync()
}

function callLogin(userId: string) {
    try {
        OS?.login?.(userId)
    } catch { /* ignore */ }
}

function callOptIn() {
    try {
        OS?.User?.pushSubscription?.optIn?.()
    } catch { /* ignore */ }
}

function callLogout() {
    try {
        OS?.logout?.()
    } catch { /* ignore */ }
}

/**
 * Link the current device subscription to the authenticated user. OneSignal docs
 * recommend calling login on every app start, and Android in particular needs
 * login AFTER the push subscription exists or the sub stays on an anonymous user.
 */
async function linkOneSignalUser(userId: string): Promise<PushState> {
    callLogin(userId)

    const deadline = Date.now() + 15000
    while (Date.now() < deadline) {
        const state = await readPushStateAsync()
        if (state.externalId === userId) return state
        await sleep(500)
    }

    return readPushStateAsync()
}

async function ensureDeviceSubscription(requestPermission: boolean): Promise<PushState> {
    let state = await readPushStateAsync()

    if (!state.permission && requestPermission) {
        try {
            await OS?.Notifications?.requestPermission?.(false)
        } catch { /* ignore */ }
        state = await readPushStateAsync()
    }

    if (!state.permission) return state

    callOptIn()
    state = await waitForPushRegistration({ requireToken: true, timeoutMs: 25_000 })

    // Permission granted and opted in but no subscription — common after OTA.
    // Force a clean re-subscribe via logout/optIn cycle.
    if (state.permission && !state.subscriptionId) {
        callLogout()
        await sleep(750)
        callOptIn()
        state = await waitForPushRegistration({ requireToken: true, timeoutMs: 25_000 })
    }

    return state
}

/**
 * Full registration pipeline:
 * 1. Ensure OS permission + device subscription/token exist
 * 2. login(external_id) to attach the subscription to the signed-in user
 * 3. If still unlinked, logout + optIn + login again (Android anonymous-user bug)
 */
export async function ensurePushRegistration(
    userId: string,
    options?: { requestPermission?: boolean },
): Promise<PushState> {
    if (!isOneSignalAvailable() || !userId) return EMPTY_PUSH_STATE

    if (registrationInFlight && registrationInFlightUserId === userId) {
        return registrationInFlight
    }

    registrationInFlightUserId = userId
    registrationInFlight = (async () => {
        let state = await ensureDeviceSubscription(Boolean(options?.requestPermission))
        if (!state.permission || !state.subscriptionId) return state

        state = await linkOneSignalUser(userId)
        if (isPushHealthy(state, userId)) return state

        // Subscription exists locally but is still on an anonymous OneSignal user.
        callLogout()
        await sleep(750)
        callOptIn()
        state = await waitForPushRegistration({ requireToken: true })
        if (!state.subscriptionId) return state

        state = await linkOneSignalUser(userId)
        return waitForPushRegistration({
            expectedExternalId: userId,
            requireToken: true,
        })
    })().finally(() => {
        registrationInFlight = null
        registrationInFlightUserId = null
    })

    return registrationInFlight
}

export function subscribeToPushStateChanges(onChange: (state: PushState) => void): () => void {
    const refresh = () => {
        void readPushStateAsync().then(onChange)
    }

    const onPermissionChange = () => refresh()
    const onSubscriptionChange = () => refresh()
    const onUserChange = () => refresh()

    try {
        OS?.Notifications?.addEventListener?.('permissionChange', onPermissionChange)
    } catch { /* ignore */ }

    try {
        OS?.User?.pushSubscription?.addEventListener?.('change', onSubscriptionChange)
    } catch { /* ignore */ }

    try {
        OS?.User?.addEventListener?.('change', onUserChange)
    } catch { /* ignore */ }

    refresh()

    return () => {
        try {
            OS?.Notifications?.removeEventListener?.('permissionChange', onPermissionChange)
        } catch { /* ignore */ }
        try {
            OS?.User?.pushSubscription?.removeEventListener?.('change', onSubscriptionChange)
        } catch { /* ignore */ }
        try {
            OS?.User?.removeEventListener?.('change', onUserChange)
        } catch { /* ignore */ }
    }
}

export async function enablePushNotifications(userId?: string): Promise<PushState> {
    if (!userId) {
        return ensureDeviceSubscription(true)
    }
    return ensurePushRegistration(userId, { requestPermission: true })
}

export async function repairPushRegistrationIfNeeded(userId?: string): Promise<PushState | null> {
    if (!isOneSignalAvailable() || !userId) return null

    const state = await readPushStateAsync()
    if (isPushHealthy(state, userId)) return state
    if (!state.permission) return state

    return ensurePushRegistration(userId, { requestPermission: false })
}

/** Wait for permission, token, subscription, and account link after OTA or SDK init. */
export async function waitForHealthyPushState(userId: string, timeoutMs = 15_000): Promise<PushState> {
    if (!isOneSignalAvailable() || !userId) return readPushStateAsync()

    return waitForPushRegistration({
        expectedExternalId: userId,
        requireToken: true,
        timeoutMs,
    })
}

/** Re-link whenever the device subscription changes while a user is signed in. */
export function bindPushSubscriptionToUser(userId: string): () => void {
    const login = () => {
        callLogin(userId)
    }

    login()

    const onSubscriptionChange = () => login()

    try {
        OS?.User?.pushSubscription?.addEventListener?.('change', onSubscriptionChange)
    } catch { /* ignore */ }

    return () => {
        try {
            OS?.User?.pushSubscription?.removeEventListener?.('change', onSubscriptionChange)
        } catch { /* ignore */ }
    }
}
